import CopyIcon from '@mui/icons-material/FileCopyOutlined';
import {
  Box,
  Button,
  Divider,
  IconButton,
  List,
  Paper,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {
  load as loadQuads,
  loadNextPage as loadNextQuadsPage,
} from '../actions/quads.js';
import {
  getFilteredQuads,
  getSelectedMosaic,
  getSelectedQuadId,
} from '../selectors/index.js';
import formatCount from '../util/format-count.js';
import ListContainer from './list.jsx';
import LoadingIndicator from './loading-indicator.jsx';
import QuadListItem from './quad-list-item.jsx';

const Wrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  overflow: 'hidden',
});

const QuadsList = ({
  loading,
  quads,
  geometry,
  loadQuads,
  mosaic,
  nextQuadsPage,
  selectedQuadId,
  loadNextQuadsPage,
}) => {
  const [quadsCopied, setQuadsCopied] = useState(false);

  useEffect(() => {
    loadQuads({geometry, mosaic});
  }, [geometry, loadQuads, mosaic]);

  const copyQuads = () => {
    const input = document.createElement('input');
    input.value = quads.map(quad => quad.id).toString();
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
    setQuadsCopied(true);
    setTimeout(setQuadsCopied(false), 1500);
  };

  if (loading && !quads.length) {
    return <LoadingIndicator>Loading quads...</LoadingIndicator>;
  }

  const quadsCount = quads.length;
  const formattedCount = formatCount(quadsCount, 'quad');

  return (
    <Wrapper>
      <Paper
        elevation={1}
        square
        sx={{
          p: 1,
          px: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="overline">Quads</Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography variant="caption" data-qe="quads-count">
            {quadsCopied
              ? `Quad${quadsCount > 1 ? 's' : ''} copied to clipboard!`
              : `${formattedCount} found`}
          </Typography>
          {quadsCount > 0 && (
            <Box
              sx={{
                ml: 1,
              }}
            >
              <Tooltip title="Copy Quad Ids">
                <IconButton
                  size="small"
                  data-qe="quad-copy-clipboard"
                  onClick={copyQuads}
                >
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Paper>

      <Divider />

      <ListContainer data-qe="all-quad-results">
        <List disablePadding>
          {quads.map(quad => (
            <QuadListItem
              selected={quad.id === selectedQuadId}
              key={quad.id}
              quad={quad}
            />
          ))}
          <Box
            sx={{
              padding: 1,
            }}
          >
            {nextQuadsPage && (
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={() => loadNextQuadsPage(nextQuadsPage)}
                disabled={loading}
              >
                {loading ? 'Loading more quads...' : 'Load More...'}
              </Button>
            )}
          </Box>
        </List>
      </ListContainer>
    </Wrapper>
  );
};

QuadsList.propTypes = {
  geometry: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  nextQuadsPage: PropTypes.string,
  mosaic: PropTypes.object,
  quads: PropTypes.array.isRequired,
  selectedQuadId: PropTypes.string,
  loadQuads: PropTypes.func.isRequired,
  loadNextQuadsPage: PropTypes.func.isRequired,
};

const mapDispatchToProps = {
  loadNextQuadsPage,
  loadQuads,
};

const mapStateToProps = state => ({
  geometry: state.geometry.json,
  loading: state.quads.loading,
  nextQuadsPage: state.quads.nextPage,
  quads: getFilteredQuads(state),
  mosaic: getSelectedMosaic(state),
  selectedQuadId: getSelectedQuadId(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(QuadsList);
